import { z } from 'zod';
import { RefinementCtx } from 'zod/lib/types';

import { OptionItemObject } from '../../../shared_models/interfaces';
import { ImportExportLevel, ImportExportOption } from '../public_model';

import { getFirstDuplicateFromList } from './utils';
import { DUPLICATE_OPTION_NAME_ERROR, INVALID_OPTION_ID_ERROR, VResult } from './Validator';



const LevelBase = z.strictObject({
    description: z.string().optional(),
    levelName: z.string().optional(),
});


const LevelSchema: z.ZodType<ImportExportLevel> = LevelBase.extend({
    options: z.lazy(() => ImportExportOptionSchema.array()),
});

const ZodTrueFalseValues = z.union([
    z.boolean(),
    z.literal('NO'),
    z.literal('YES'),
    z.literal('no'),
    z.literal('yes'),
    z.literal('No'),
    z.literal('Yes'),
    z.literal('n'),
    z.literal('y'),
    z.literal('N'),
    z.literal('Y'),
    z.literal('1'),
    z.literal('0'),
    z.literal('False'),
    z.literal('True'),
    z.literal('false'),
    z.literal('true'),
    z.literal('FALSE'),
    z.literal('TRUE'),
    z.literal('F'),
    z.literal('T'),
    z.literal('f'),
    z.literal('t'),
]);

const  ImportExportOptionBeseSchema = z.strictObject(
    {
        value: z.string(),
        id: z.string().optional(),
        isDefault: ZodTrueFalseValues.optional(),
        isEnabled: ZodTrueFalseValues.optional(),
    });

const ImportExportOptionSchema: z.ZodType<ImportExportOption> = ImportExportOptionBeseSchema.extend({
    nestedLevel: z.lazy(() => LevelSchema.optional()),
});

export const convertToIssues = (results: VResult[], ctx: RefinementCtx, message:string) => {
    if (results.length) {
        for (const error of results) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:message ,
                params: [{ invalidValue: error.invalidValue }],
                // @ts-ignore
                path: [...error.path],
            });
        }
    }

};
const validateUniquenessOfOptionName = ( level: ImportExportLevel, ctx: RefinementCtx) => {
    const results = doValidateUniquenessOfOptionName(level?.options || []);
    convertToIssues(results, ctx,DUPLICATE_OPTION_NAME_ERROR);
};

const doValidateUniquenessOfOptionName = (options: ImportExportOption[], validationResult: VResult[] = [], paths: string[]=[]) => {
    const duplicate = getFirstDuplicateFromList(options.map(option => option.value));
    const iduplicateIndex = options.findIndex((option) => option.value === duplicate);
    if (duplicate) {
        validationResult.push({
            path: [...paths, `options.${iduplicateIndex}.nestedLevel`, 'value'],
            invalidValue: duplicate,
        });
    }
    let index =0;
    for (const entry of options) {
        if (entry.nestedLevel?.options?.length) {
            doValidateUniquenessOfOptionName(entry.nestedLevel.options, validationResult,[...paths,`options.${index}.nestedLevel`]);
        }
        index++;
    }
    return validationResult;
};

const validaOptionIds = (model: OptionItemObject[]) => (firstLevel: ImportExportLevel, ctx: RefinementCtx) => {
    const results = doValidaOptionIds(model, firstLevel.options, [], []);
    if (results.length) {
        for (const error of results) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: INVALID_OPTION_ID_ERROR,
                params: [{ invalidValue: error.id }],
                path: error.path,
            });
        }
    }
};

const getOptionById = (options: OptionItemObject[], id: string): OptionItemObject | undefined => {
    return options.find(item => item.key === id);
};

const getCorrespondingLevel = (option: ImportExportOption, configOptions: OptionItemObject[]): OptionItemObject[] => {
    const items = configOptions.find(item => item.key === option.id)?.children;
    return items ? items : [];
};

const doValidaOptionIds = (
    configOptions: OptionItemObject[],
    options: ImportExportOption[]=[],
    validationResult: VResult[] = [],
    parentIndexs: string[]
): VResult[] => {
    options.forEach(option => {
        let index = 0;
        if (option.id) {
            const foundOption = getOptionById(configOptions, option.id);

            if (!foundOption) {
                validationResult.push({ id: option.id, path:[...parentIndexs, `options.${index}.nestedLevel`, 'id'] });
            }
            if (option.nestedLevel?.options?.length) {
                doValidaOptionIds(getCorrespondingLevel(option, configOptions), option.nestedLevel?.options, validationResult,
                    [...parentIndexs,`options.${index}.nestedLevel`]);
            }
        }
        index++;
    });
    return validationResult;
};

const atLeastOneOptionOnLevelOne = (firstLevel: ImportExportLevel) => {
    return firstLevel?.options && firstLevel.options.length > 0 &&  firstLevel.options[0].value;
};

const atLeastOneOptionOnLevelOneMessage = {
    message: 'Json file should have at least one option defined on first level',
};
export const getImportExportJsonZod = (model: OptionItemObject[]) =>
    z.object({

        version: z.string(),
        firstLevel: LevelSchema
            .superRefine(validateUniquenessOfOptionName)
            .refine(atLeastOneOptionOnLevelOne, atLeastOneOptionOnLevelOneMessage)
            .superRefine(validaOptionIds(model))
            ,
    });

// @ts-ignore
const ImportExportJsonZod = getImportExportJsonZod({ treeData: [] });
export type ImportExportJsonZodType = z.infer<typeof ImportExportJsonZod>;
